import type { Form, FormListItem, FormSchema, FormSettings } from '../types/form';
import type { Submission, PresignedUploadResponse } from '../types/submission';
import { useAuthStore } from '../stores/authStore';

const API_BASE = '/api';

/**
 * CSRF Token Management
 * Uses double-submit cookie pattern
 */
const CSRF_COOKIE_NAME = 'tfm_csrf';
const CSRF_HEADER_NAME = 'x-csrf-token';

/**
 * Get CSRF token from cookie
 */
function getCSRFToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Ensure CSRF token is available
 * Fetches a new token if not present
 */
async function ensureCSRFToken(): Promise<void> {
  const token = getCSRFToken();
  if (!token) {
    try {
      await fetch(`${API_BASE}/csrf`);
    } catch (error) {
      console.warn('Failed to fetch CSRF token:', error);
    }
  }
}

/**
 * Handle 401 Unauthorized responses globally
 * Clears session state and redirects to login page when session expires
 */
function handleUnauthorized(): void {
  // Only redirect if we're not already on a public page
  const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/invite', '/p/', '/q/', '/l/', '/f/', '/play', '/results/'];
  const currentPath = window.location.pathname;

  const isPublicPage = publicPaths.some(path => currentPath.startsWith(path));

  if (!isPublicPage) {
    // Clear auth state to prevent redirect loop
    const authStore = useAuthStore.getState();
    authStore.logout().catch(() => {
      // Ignore logout errors - session is already invalid
      console.warn('Session already expired');
    });

    // Small delay to ensure state is cleared before redirect
    setTimeout(() => {
      window.location.href = `/login?from=${encodeURIComponent(currentPath)}`;
    }, 100);
  }
}

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  // Add CSRF token to state-changing requests
  const method = options.method?.toUpperCase() || 'GET';
  const needsCSRF = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (needsCSRF) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // Handle session expiration globally
    if (response.status === 401) {
      handleUnauthorized();
      throw new Error('Session expired');
    }

    const errorData = await response.json().catch(() => ({ error: 'Request failed' })) as { error?: string };
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

// Forms API
export async function listForms(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<{ forms: FormListItem[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  return fetchAPI(`/forms${query ? `?${query}` : ''}`);
}

export async function getForm(id: string): Promise<Form> {
  return fetchAPI(`/forms/${id}`);
}

export async function createForm(data: { name: string; description?: string }): Promise<{ id: string; slug: string; name: string }> {
  return fetchAPI('/forms', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateForm(
  id: string,
  data: { name?: string; description?: string; schema?: FormSchema; settings?: FormSettings }
): Promise<{ success: boolean; version: number }> {
  return fetchAPI(`/forms/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteForm(id: string): Promise<{ success: boolean }> {
  return fetchAPI(`/forms/${id}`, {
    method: 'DELETE',
  });
}

export async function publishForm(id: string): Promise<{ success: boolean; slug: string; publicUrl: string; resultsUrl: string; resultsKey: string }> {
  return fetchAPI(`/forms/${id}/publish`, {
    method: 'POST',
  });
}

// Submissions API
export async function listSubmissions(
  formId: string,
  params?: { status?: string; page?: number; limit?: number }
): Promise<{ submissions: Submission[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));

  const query = searchParams.toString();
  return fetchAPI(`/forms/${formId}/submissions${query ? `?${query}` : ''}`);
}

export async function submitForm(
  formId: string,
  data: Record<string, unknown>,
  uploadIds?: string[]
): Promise<{ success: boolean; submissionId: string; message: string; redirectUrl?: string }> {
  return fetchAPI(`/forms/${formId}/submissions`, {
    method: 'POST',
    body: JSON.stringify({ data, uploadIds }),
  });
}

export async function exportSubmissions(
  formId: string,
  format: 'json' | 'csv' = 'json',
  status?: string
): Promise<Blob | object> {
  const searchParams = new URLSearchParams({ format });
  if (status) searchParams.set('status', status);

  const response = await fetch(`${API_BASE}/forms/${formId}/submissions/export?${searchParams}`);

  if (!response.ok) {
    throw new Error('Export failed');
  }

  if (format === 'csv') {
    return response.blob();
  }

  return response.json();
}

// File Upload API
export async function getPresignedUrl(data: {
  formId: string;
  fieldId: string;
  filename: string;
  contentType: string;
  size: number;
  fieldType?: string;
}): Promise<PresignedUploadResponse & { objectKey: string; uploadUrl: string }> {
  return fetchAPI('/upload/presign', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function uploadFile(
  uploadId: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<{ success: boolean; uploadId: string; path: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API_BASE}/upload/${uploadId}`);
    xhr.setRequestHeader('Content-Type', file.type);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText));
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export function getFileUrl(uploadId: string): string {
  return `${API_BASE}/upload/${uploadId}`;
}

// Form type prefixes for public URLs
export type FormTypePrefix = 'f' | 'p' | 'q' | 'l';

// Public Form API
export async function getPublicForm(slug: string, typePrefix: FormTypePrefix = 'f'): Promise<Form & { form_type?: string; preferredUrl?: string; activeSession?: unknown }> {
  const response = await fetch(`/api/${typePrefix}/${slug}`);
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { error?: string; redirect?: string };
    if (data.redirect) {
      // Form exists but at a different URL
      throw new Error(`REDIRECT:${data.redirect}`);
    }
    throw new Error('Form not found');
  }
  return response.json();
}

// Save & Resume API
export interface SavedProgress {
  found: boolean;
  data?: Record<string, unknown>;
  currentStep?: number;
  progressPercent?: number;
  resumeToken?: string;
  expiresAt?: string;
  lastUpdated?: string;
}

export interface SaveProgressResult {
  success: boolean;
  resumeToken: string;
  progressPercent: number;
  expiresAt: string;
}

export async function getSavedProgress(
  formId: string,
  params?: { token?: string; email?: string }
): Promise<SavedProgress> {
  const searchParams = new URLSearchParams();
  if (params?.token) searchParams.set('token', params.token);
  if (params?.email) searchParams.set('email', params.email);

  const query = searchParams.toString();
  return fetchAPI(`/forms/${formId}/save${query ? `?${query}` : ''}`);
}

export async function saveProgress(
  formId: string,
  data: {
    data: Record<string, unknown>;
    currentStep?: number;
    resumeToken?: string;
    email?: string;
  }
): Promise<SaveProgressResult> {
  return fetchAPI(`/forms/${formId}/save`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteSavedProgress(
  formId: string,
  token: string
): Promise<{ success: boolean }> {
  return fetchAPI(`/forms/${formId}/save?token=${encodeURIComponent(token)}`, {
    method: 'DELETE',
  });
}

/**
 * Wrapper for fetch that handles 401 responses and CSRF tokens
 */
async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  // Add CSRF token to state-changing requests
  const method = options?.method?.toUpperCase() || 'GET';
  const needsCSRF = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

  const headers: Record<string, string> = {
    ...(options?.headers as Record<string, string>),
  };

  if (needsCSRF) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      headers[CSRF_HEADER_NAME] = csrfToken;
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle session expiration globally
  if (response.status === 401) {
    handleUnauthorized();
  }

  return response;
}

// Generic API helper object for direct HTTP method calls
export const api = {
  async get(endpoint: string, options?: RequestInit): Promise<Response> {
    return fetchWithAuth(endpoint, {
      ...options,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  },

  async post(endpoint: string, data?: unknown, options?: RequestInit): Promise<Response> {
    return fetchWithAuth(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async put(endpoint: string, data?: unknown, options?: RequestInit): Promise<Response> {
    return fetchWithAuth(endpoint, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async patch(endpoint: string, data?: unknown, options?: RequestInit): Promise<Response> {
    return fetchWithAuth(endpoint, {
      ...options,
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  async delete(endpoint: string, options?: RequestInit): Promise<Response> {
    return fetchWithAuth(endpoint, {
      ...options,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });
  },
};

// Live Quiz Session API
export interface LiveQuizSession {
  sessionId: string;
  gamePin: string;
  websocketUrl: string;
  totalQuestions: number;
}

export async function createLiveQuizSession(formId: string): Promise<LiveQuizSession> {
  return fetchAPI('/live-quiz/sessions', {
    method: 'POST',
    body: JSON.stringify({ formId }),
  });
}

export async function joinLiveQuiz(gamePin: string, nickname: string, avatar?: string): Promise<{
  participantId: string;
  sessionId: string;
  websocketUrl: string;
  token: string;
}> {
  return fetchAPI('/live-quiz/join', {
    method: 'POST',
    body: JSON.stringify({ gamePin, nickname, avatar }),
  });
}

// Blog API
export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content: string;
  featured_image: string | null;
  author_id: string;
  category: string;
  tags: string[];
  status: string;
  meta_title: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  reading_time_minutes: number;
  view_count: number;
  locale: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogListResponse {
  posts: BlogPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function listBlogPosts(params?: {
  page?: number;
  limit?: number;
  category?: string;
  locale?: string;
}): Promise<BlogListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.category) searchParams.set('category', params.category);
  if (params?.locale) searchParams.set('locale', params.locale);

  const query = searchParams.toString();
  return fetchAPI(`/blog${query ? `?${query}` : ''}`);
}

export async function getBlogPost(slug: string, locale?: string): Promise<{ post: BlogPost }> {
  const searchParams = new URLSearchParams();
  if (locale) searchParams.set('locale', locale);
  const query = searchParams.toString();
  return fetchAPI(`/blog/${slug}${query ? `?${query}` : ''}`);
}

export async function listBlogPostsAdmin(params?: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<BlogListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.limit) searchParams.set('limit', String(params.limit));
  if (params?.status) searchParams.set('status', params.status);

  const query = searchParams.toString();
  return fetchAPI(`/blog/admin${query ? `?${query}` : ''}`);
}

export async function createBlogPost(data: {
  title: string;
  content: string;
  slug?: string;
  excerpt?: string;
  featured_image?: string;
  category?: string;
  tags?: string[];
  status?: 'draft' | 'published';
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  locale?: string;
}): Promise<{ success: boolean; post: { id: string; slug: string; title: string; status: string } }> {
  return fetchAPI('/blog', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBlogPost(slug: string, data: {
  title?: string;
  content?: string;
  slug?: string;
  excerpt?: string;
  featured_image?: string;
  category?: string;
  tags?: string[];
  status?: 'draft' | 'published' | 'archived';
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  locale?: string;
}): Promise<{ success: boolean; post: { id: string; slug: string } }> {
  return fetchAPI(`/blog/${slug}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBlogPost(slug: string): Promise<{ success: boolean }> {
  return fetchAPI(`/blog/${slug}`, {
    method: 'DELETE',
  });
}

/**
 * Initialize CSRF protection
 * Call this when the app loads to ensure CSRF token is available
 */
export { ensureCSRFToken };
